import {JourneyStepStatus, useSequenceJourneyState} from '@/modules/patients-therapies/hooks'
import {Separator, Steps, StepsItemProps} from '@chakra-ui/react'
import {PatientTherapySequence} from '@cypro/engine/schema'
import {Tooltip} from '@cypro/ui/components'
import {useTranslate} from '@tolgee/react'
import {LuCheck, LuDot, LuX} from 'react-icons/lu'

interface Props {
  sequence: PatientTherapySequence
}

export function TherapySequenceJourneyTimeline({sequence}: Props) {
  const {getStepState, orderedSteps} = useSequenceJourneyState(sequence)

  const items = orderedSteps.map(step => ({label: step.toLowerCase(), step, ...getStepState(step)}))

  return <TherapySequenceJourneyTimelineInternal items={items} />
}

export function TherapySequenceJourneyTimelineInternal({
  items,
}: {
  items: {
    status: JourneyStepStatus
    color: string
    label: string
    step: 'DOCTOR_VALIDATION' | 'VALIDATION' | 'PATIENT_ARRIVAL' | 'PRODUCTION' | 'ADMINISTRATION'
  }[]
}) {
  const {t} = useTranslate('common')

  return (
    <Steps.Root size="xs" variant="subtle" position="relative">
      <Separator
        position="absolute"
        top="50%"
        right="50%"
        transform="translateX(50%)"
        width="80%"
        colorPalette="gray.muted"
      />

      <Steps.List display="grid" justifyItems="center" gridTemplateColumns={`repeat(${items.length}, 1fr)`}>
        {items.map((item, index) => (
          <Tooltip key={t(item.label)} content={`${t(item.label)} - ${t(item.status)}`}>
            <Step index={index} status={item.status} color={item.color} gridColumn={index + 1} />
          </Tooltip>
        ))}
      </Steps.List>
    </Steps.Root>
  )
}

interface StepProps extends StepsItemProps {
  status: JourneyStepStatus
}

function Step({status, ...props}: StepProps) {
  let indicatorProps: Omit<StepsItemProps, 'index'> = {
    background: 'gray.emphasized',
  }

  if (status !== 'pending') {
    indicatorProps = {
      ...indicatorProps,
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: props.color,
      background: props.color,
    }
  }

  if (status === 'in-progress') {
    const [baseColor] = typeof props.color === 'string' ? props.color?.split('.') || [] : []
    if (baseColor) indicatorProps = {...indicatorProps, background: `${baseColor}.muted`}
  }

  if (status === 'finished' || status === 'failed' || status === 'cancelled') {
    indicatorProps = {...indicatorProps, color: 'white'}
  }

  return (
    <Steps.Item {...props} width="4" height="4">
      <Steps.Indicator
        {...indicatorProps}
        width="inherit"
        height="inherit"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {status === 'in-progress' && <LuDot />}
        {status === 'finished' && <LuCheck />}
        {(status === 'failed' || status === 'cancelled') && <LuX size={10} />}
      </Steps.Indicator>
    </Steps.Item>
  )
}
